import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircleIcon, ClockIcon, LockClosedIcon, MagnifyingGlassIcon, ShieldCheckIcon, TrashIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { blacklistAPI, Client, ClientClaim, clientsAPI, PermanentBlacklistEntry } from '../../services/api';
import { Button, Modal, Spinner } from '../ui';

type AdminTab = 'CLAIMS' | 'PERMANENT';

const AdminBlacklistClaims: React.FC = () => {
  const [tab, setTab] = useState<AdminTab>('CLAIMS');
  const [claims, setClaims] = useState<ClientClaim[]>([]);
  const [permanentClients, setPermanentClients] = useState<PermanentBlacklistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<ClientClaim | null>(null);
  const [decision, setDecision] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [note, setNote] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientOptions, setClientOptions] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [protectionReason, setProtectionReason] = useState('');
  const [unlockTarget, setUnlockTarget] = useState<PermanentBlacklistEntry | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextClaims, protectedClients] = await Promise.all([
        blacklistAPI.getAdminClaims(),
        blacklistAPI.getPermanent(),
      ]);
      setClaims(nextClaims);
      setPermanentClients(protectedClients);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load blacklist administration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== 'PERMANENT' || selectedClient || !clientSearch.trim()) {
      setClientOptions([]);
      return;
    }
    const timer = window.setTimeout(async () => {
      try {
        const clients = await clientsAPI.search(clientSearch.trim());
        setClientOptions(clients.slice(0, 8));
      } catch {
        setClientOptions([]);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [clientSearch, selectedClient, tab]);

  const openClaim = (claim: ClientClaim, nextDecision: 'APPROVED' | 'REJECTED') => {
    setSelectedClaim(claim);
    setDecision(nextDecision);
    setNote('');
  };

  const submitClaim = async () => {
    if (!selectedClaim) return;
    setSaving(true);
    try {
      await blacklistAPI.reviewClaim(selectedClaim.id, decision, note || undefined);
      setSelectedClaim(null);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not review this claim');
    } finally {
      setSaving(false);
    }
  };

  const protectClient = async () => {
    if (!selectedClient) {
      setError('Choose an existing customer first.');
      return;
    }
    setSaving(true);
    try {
      await blacklistAPI.protectClient(selectedClient.id, protectionReason.trim() || undefined);
      setSelectedClient(null);
      setClientSearch('');
      setProtectionReason('');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not permanently protect this customer');
    } finally {
      setSaving(false);
    }
  };

  const unlockClient = async () => {
    if (!unlockTarget) return;
    setSaving(true);
    try {
      await blacklistAPI.unprotectClient(unlockTarget.id);
      setUnlockTarget(null);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not remove permanent protection');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-full bg-neutral-50 p-4 sm:p-6 lg:p-8">
      <header className="rounded-3xl bg-gradient-to-r from-slate-950 to-slate-700 p-6 text-white shadow-xl sm:p-8">
        <div className="flex items-center gap-4"><div className="rounded-2xl bg-white/10 p-3"><ShieldCheckIcon className="h-8 w-8 text-cyan-300" /></div><div><h1 className="text-2xl font-black text-white sm:text-3xl">Blacklist Administration</h1><p className="mt-1 text-sm text-slate-300">Review ownership claims or permanently protect customer relationships.</p></div></div>
        <div className="mt-6 inline-flex rounded-2xl bg-white/10 p-1">
          <button onClick={() => setTab('CLAIMS')} className={`rounded-xl px-4 py-2 text-sm font-bold transition ${tab === 'CLAIMS' ? 'bg-white text-slate-950' : 'text-slate-300 hover:text-white'}`}>Claim requests</button>
          <button onClick={() => setTab('PERMANENT')} className={`rounded-xl px-4 py-2 text-sm font-bold transition ${tab === 'PERMANENT' ? 'bg-black text-white' : 'text-slate-300 hover:text-white'}`}>Permanent Protection</button>
        </div>
      </header>

      {error && <div className="mt-5 flex items-start justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"><span>{error}</span><button onClick={() => setError('')} className="font-bold">Dismiss</button></div>}
      {loading ? <div className="flex min-h-[25rem] items-center justify-center"><Spinner size="lg" /></div> : tab === 'CLAIMS' ? (
        <div className="mt-6 space-y-4">
          {claims.map((claim) => {
            const requester = claim.requestedBy ? [claim.requestedBy.firstName, claim.requestedBy.lastName].filter(Boolean).join(' ') || claim.requestedBy.email : 'Sales user';
            const lastDeal = claim.client.deals?.[0]?.createdAt;
            return <article key={claim.id} className="flex flex-col gap-5 rounded-3xl border border-neutral-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between"><div className="flex items-start gap-4"><div className="rounded-2xl bg-amber-100 p-3"><ClockIcon className="h-6 w-6 text-amber-700" /></div><div><h2 className="text-lg font-bold text-neutral-900">{claim.client.name}</h2><p className="text-sm text-neutral-600">Requested by <span className="font-semibold">{requester}</span></p><p className="mt-1 text-xs text-neutral-400">Requested {new Date(claim.createdAt).toLocaleString()} · {lastDeal ? `Last deal ${new Date(lastDeal).toLocaleDateString()}` : 'No previous deal'}</p></div></div><div className="flex gap-2"><Button variant="danger" leftIcon={<XCircleIcon />} onClick={() => openClaim(claim, 'REJECTED')}>Reject</Button><Button variant="success" leftIcon={<CheckCircleIcon />} onClick={() => openClaim(claim, 'APPROVED')}>Approve</Button></div></article>;
          })}
          {claims.length === 0 && <div className="rounded-3xl border border-dashed border-neutral-300 bg-white p-16 text-center"><CheckCircleIcon className="mx-auto h-12 w-12 text-emerald-400" /><p className="mt-3 font-bold text-neutral-700">No pending claims</p></div>}
        </div>
      ) : (
        <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(20rem,.8fr)_minmax(0,1.2fr)]">
          <section className="rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3"><div className="rounded-2xl bg-black p-3"><LockClosedIcon className="h-6 w-6 text-white" /></div><div><h2 className="text-lg font-black text-neutral-950">Protect a customer</h2><p className="text-sm text-neutral-500">Choose an existing CRM customer.</p></div></div>
            <div className="relative mt-6">
              <MagnifyingGlassIcon className="absolute left-3 top-3.5 h-5 w-5 text-neutral-400" />
              <input value={clientSearch} onChange={(event) => { setClientSearch(event.target.value); setSelectedClient(null); }} placeholder="Search existing customers..." className="w-full rounded-2xl border border-neutral-300 py-3 pl-10 pr-4 text-sm focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-200" />
              {clientOptions.length > 0 && !selectedClient && <div className="absolute z-20 mt-2 max-h-64 w-full overflow-auto rounded-2xl border border-neutral-200 bg-white p-2 shadow-xl">{clientOptions.map((client) => <button key={client.id} onClick={() => { setSelectedClient(client); setClientSearch(client.name); setClientOptions([]); }} className="block w-full rounded-xl px-3 py-2.5 text-left hover:bg-neutral-100"><span className="block text-sm font-bold text-neutral-900">{client.name}</span><span className="block text-xs text-neutral-500">{client.email || 'No email on file'}</span></button>)}</div>}
            </div>
            {selectedClient && <div className="mt-3 rounded-2xl border border-neutral-200 bg-neutral-50 p-3 text-sm"><span className="font-bold text-neutral-900">Selected:</span> {selectedClient.name}</div>}
            <label className="mt-5 block text-sm font-semibold text-neutral-700">Protection reason (optional)<textarea value={protectionReason} onChange={(event) => setProtectionReason(event.target.value)} maxLength={1000} rows={4} className="mt-2 w-full rounded-2xl border border-neutral-300 p-3 font-normal focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-200" placeholder="Internal context for administrators..." /></label>
            <Button onClick={protectClient} isLoading={saving} disabled={!selectedClient} className="mt-5 w-full bg-black text-white hover:bg-neutral-800" leftIcon={<LockClosedIcon />}>Protect permanently</Button>
            <p className="mt-3 text-xs leading-5 text-neutral-500">Any pending ownership claim will be rejected. The current salesperson keeps access, and the assignment cannot change until protection is removed.</p>
          </section>

          <section>
            <div className="mb-4 flex items-end justify-between"><div><h2 className="text-xl font-black text-neutral-950">Permanently protected</h2><p className="text-sm text-neutral-500">{permanentClients.length} customer{permanentClients.length === 1 ? '' : 's'}</p></div></div>
            <div className="space-y-3">{permanentClients.map((client) => {
              const protectedBy = client.permanentlyProtectedBy ? [client.permanentlyProtectedBy.firstName, client.permanentlyProtectedBy.lastName].filter(Boolean).join(' ') || client.permanentlyProtectedBy.email : 'Administrator';
              return <article key={client.id} className="rounded-3xl bg-black p-5 text-white shadow-lg ring-1 ring-white/10"><div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"><div><div className="flex flex-wrap items-center gap-2"><h3 className="text-lg font-black text-white">{client.name}</h3><span className="rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-xs font-bold text-white">Permanently protected</span></div><p className="mt-2 text-sm text-neutral-300">{client.permanentProtectionReason || 'No reason provided'}</p><p className="mt-2 text-xs text-neutral-500">Protected by {protectedBy} · {new Date(client.permanentlyProtectedAt).toLocaleString()}</p></div><Button variant="ghost" onClick={() => setUnlockTarget(client)} className="border border-white/20 text-white hover:bg-white/10" leftIcon={<TrashIcon />}>Remove protection</Button></div></article>;
            })}{permanentClients.length === 0 && <div className="rounded-3xl border border-dashed border-neutral-300 bg-white p-16 text-center"><LockClosedIcon className="mx-auto h-12 w-12 text-neutral-300" /><p className="mt-3 font-bold text-neutral-700">No permanently protected customers</p></div>}</div>
          </section>
        </div>
      )}

      <Modal isOpen={Boolean(selectedClaim)} onClose={() => setSelectedClaim(null)} title={`${decision === 'APPROVED' ? 'Approve' : 'Reject'} claim`} description={selectedClaim ? `${selectedClaim.client.name} · ${selectedClaim.requestedBy?.email || ''}` : ''} footer={<><Button variant="ghost" onClick={() => setSelectedClaim(null)}>Cancel</Button><Button variant={decision === 'APPROVED' ? 'success' : 'danger'} isLoading={saving} onClick={submitClaim}>{decision === 'APPROVED' ? 'Approve transfer' : 'Reject request'}</Button></>}>
        <label className="text-sm font-semibold text-neutral-700">Review note (optional)<textarea value={note} onChange={(event) => setNote(event.target.value)} rows={4} className="mt-2 w-full rounded-xl border border-neutral-300 p-3 font-normal focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" /></label>
      </Modal>

      <Modal isOpen={Boolean(unlockTarget)} onClose={() => setUnlockTarget(null)} title="Remove permanent protection?" description={unlockTarget ? `${unlockTarget.name} will immediately return to the normal 60-day availability rules.` : ''} footer={<><Button variant="ghost" onClick={() => setUnlockTarget(null)}>Cancel</Button><Button variant="danger" isLoading={saving} onClick={unlockClient}>Remove protection</Button></>}>
        <p className="text-sm leading-6 text-neutral-600">This does not reassign the customer. It only allows future claims or assignment changes according to the normal rules.</p>
      </Modal>
    </div>
  );
};

export default AdminBlacklistClaims;
